import { useEffect, useState } from 'react';
import { X, Crown, Check, Sparkles, Heart, Dices, Flame, BookmarkCheck, Copy, ExternalLink, Clock } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import SaveProgressModal from './SaveProgressModal';
import { LegalLinks } from './LegalLinks';

const FEATURES = {
  dice: { icon: Dices, name: 'Dice Rolls', limit: 10 },
  tod: { icon: Flame, name: 'Truth or Dare', limit: 10 },
  positions: { icon: Heart, name: 'Intimacy reveals', limit: 5 },
};

const API = process.env.REACT_APP_BACKEND_URL;

export default function Paywall() {
  const {
    showPaywall,
    setShowPaywall,
    paywallFeature,
    isPremium,
    userEmail,
    visitorId,
    payment,
    savePayment,
    refreshPayment,
    paymentError,
    checkingPayment,
  } = useSubscription();

  // 4-step flow: 'intro' → 'details' → 'pay' → 'pending'
  const [step, setStep] = useState('intro');
  const [formEmail, setFormEmail] = useState(userEmail || '');
  const [formPhone, setFormPhone] = useState('');
  const order = payment;
  const [utr, setUtr] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (showPaywall && payment) {
      setFormEmail(payment.email || '');
      setFormPhone(payment.phone || '');
      setStep(payment.status === 'initiated' ? 'pay' : 'pending');
    }
  }, [showPaywall, payment]);

  if (!showPaywall || isPremium) return null;

  const feature = FEATURES[paywallFeature] || FEATURES.dice;
  const FeatureIcon = feature.icon;

  const closePaywall = () => {
    setStep('intro');
    setError(null);
    setLoading(false);
    setShowPaywall(false);
  };

  // ── Step 1 → 2: User clicked "Continue" ─────────────────────────────────────
  const goToDetails = () => { setStep('details'); setError(null); };

  // ── Step 2 → 3: Submit email+phone, create order, show UPI ──────────────────
  const submitDetails = async (e) => {
    e?.preventDefault?.();
    const email = formEmail.trim().toLowerCase();
    const phone = formPhone.replace(/\D/g, '');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (phone.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/upi/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId, email, phone }),
      });
      const data = await res.json();
      if (!res.ok || !data.upi_link) {
        setError(data.detail || 'Could not start payment. Please try again.');
        setLoading(false);
        return;
      }
      savePayment({ ...data, email, phone, visitor_id: visitorId, status: 'initiated' });
      setStep('pay');
    } catch (err) {
      console.error('[Paywall] initiate error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3a: Open UPI app ───────────────────────────────────────────────────
  const openUpi = () => { if (order?.upi_link) window.location.href = order.upi_link; };

  // ── Step 3b: Copy UPI ID ────────────────────────────────────────────────────
  const copyUpiId = async () => {
    try { await navigator.clipboard.writeText(order.payee_vpa); setCopied(true); }
    catch { setError('Could not copy. Please select the UPI ID and copy it manually.'); }
  };

  // ── Step 3 → 4: Submit UTR ──────────────────────────────────────────────────
  const submitUtr = async (e) => {
    e?.preventDefault?.();
    const utrClean = utr.replace(/\D/g, '');
    if (utrClean.length !== 12) {
      setError('UTR must be the 12-digit reference number from your UPI app.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/upi/submit-utr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId, order_id: order.order_id, utr: utrClean }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Could not submit. Please try again.');
        setLoading(false);
        return;
      }
      setStep('pending');
      savePayment({ ...order, status: data.status, utr: utrClean });
    } catch (err) {
      console.error('[Paywall] UTR submit error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      role="dialog" aria-modal="true" aria-label="LovePair Premium"
      data-testid="paywall-modal"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-[#FF8FAF] via-[#FF6B9D] to-[#F4436C] p-6 text-white">
          <button
            onClick={closePaywall}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
            data-testid="paywall-close-btn"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
              <Crown size={24} />
            </div>
            <div>
              <p className="text-white/80 text-xs font-medium">Unlock</p>
              <h2 className="text-xl font-bold">LovePair Premium</h2>
            </div>
          </div>
          {step === 'intro' && (
            <p className="text-white/90 text-sm">
              {paywallFeature ? `You've used all your free ${feature.name.toLowerCase()}!` : 'More moments together, with unlimited access.'}
            </p>
          )}
        </div>

        <div className="p-5">
          {/* ─── STEP 1: INTRO ─── */}
          {step === 'intro' && (
            <>
              <div className="bg-[#FFF0F3] rounded-2xl p-4 mb-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF8FAF] to-[#F4436C] flex items-center justify-center flex-shrink-0">
                  <FeatureIcon size={20} color="white" />
                </div>
                <div>
                  <p data-testid="paywall-feature-message" className="text-[#4A2C35] font-semibold text-sm">{paywallFeature ? `${feature.limit} free ${feature.name.toLowerCase()} used` : 'Unlimited couple activities'}</p>
                  <p className="text-[#8B6B75] text-xs">Subscribe for unlimited access</p>
                </div>
              </div>

              <div className="space-y-3 mb-5">
                <p className="text-[#4A2C35] font-semibold text-sm">Premium Benefits:</p>
                {['Unlimited Dice Rolls', 'Unlimited Truth or Dare', 'Unlimited Intimacy reveals', 'No Ads Ever', 'Priority Support'].map((b) => (
                  <div key={b} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check size={12} className="text-green-600" />
                    </div>
                    <span className="text-[#4A2C35] text-sm">{b}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-[#FFF8F9] to-[#FFF0F3] rounded-2xl p-4 mb-5 text-center border border-[#FFE0E8]">
                <p className="text-[#8B6B75] text-xs mb-1">Only</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl font-bold text-[#F4436C]">₹69</span>
                  <span className="text-[#8B6B75] text-sm">/ 2 months</span>
                </div>
                <p data-testid="paywall-billing-note" className="text-[#8B6B75] text-xs mt-1">One-time UPI payment · 60 days from approval</p>
              </div>

              <button
                onClick={goToDetails}
                data-testid="paywall-subscribe-btn"
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#FF8FAF] via-[#FF6B9D] to-[#F4436C] text-white font-bold text-base shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                style={{ boxShadow: '0 4px 15px rgba(244, 67, 108, 0.4)' }}
              >
                <Sparkles size={18} />
                Continue to Pay ₹69
              </button>
              <p className="text-[10px] text-center text-[#B08EA0] mt-2">GPay · PhonePe · Paytm · CRED · BHIM supported</p>

              <button data-testid="paywall-maybe-later" onClick={closePaywall} className="w-full py-3 mt-3 text-[#8B6B75] text-sm font-medium">Maybe later</button>

              <div className="flex items-center gap-2 my-2">
                <div className="flex-1 h-px bg-[#FFE0E8]" />
                <span className="text-[10px] text-[#B08EA0]">or</span>
                <div className="flex-1 h-px bg-[#FFE0E8]" />
              </div>

              <button
                onClick={() => setShowSaveModal(true)}
                data-testid="paywall-save-progress-btn"
                className="w-full py-2.5 rounded-2xl border border-[#FFE0E8] bg-[#FFF8F9] text-[#8B6B75] text-xs font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
              >
                <BookmarkCheck size={14} color="#F4436C" />
                {userEmail ? `Progress saved (${userEmail})` : 'Save progress with email'}
              </button>
            </>
          )}

          {/* ─── STEP 2: DETAILS ─── */}
          {step === 'details' && (
            <form onSubmit={submitDetails} className="space-y-4" data-testid="upi-details-form">
              <div className="bg-gradient-to-br from-[#FFF8F9] to-[#FFF0F3] rounded-2xl p-4 text-center border border-[#FFE0E8]">
                <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-[#FF8FAF] to-[#F4436C] flex items-center justify-center mb-2">
                  <Sparkles size={22} color="white" />
                </div>
                <p className="font-bold text-[#4A2C35] text-sm">Almost there! 🎉</p>
                <p className="text-[11px] text-[#8B6B75] mt-1 leading-snug">Your details are used for payment verification and support.</p>
              </div>

              <div>
                <label className="text-[11px] font-semibold text-[#8B6B75] uppercase tracking-wider">Email</label>
                <input
                  type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="you@example.com" required data-testid="upi-details-email"
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-[#FFE0E8] bg-white text-[#4A2C35] text-sm focus:outline-none focus:border-[#F4436C]"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#8B6B75] uppercase tracking-wider">Phone (UPI registered)</label>
                <input
                  type="tel" value={formPhone} onChange={(e) => setFormPhone(e.target.value)}
                  placeholder="10-digit mobile number" inputMode="numeric" required data-testid="upi-details-phone"
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-[#FFE0E8] bg-white text-[#4A2C35] text-sm focus:outline-none focus:border-[#F4436C]"
                />
              </div>

              {error && <div className="bg-red-50 text-red-600 text-xs rounded-xl p-3 text-center" data-testid="paywall-error">{error}</div>}

              <button
                type="submit" disabled={loading || !visitorId}
                data-testid="upi-details-submit"
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#FF8FAF] to-[#F4436C] text-white font-bold text-base shadow-lg active:scale-[0.98] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles size={18} />}
                {loading ? 'Setting up…' : 'Continue to Payment'}
              </button>

              <button data-testid="upi-details-back" type="button" onClick={() => setStep('intro')} className="w-full py-2 text-[#8B6B75] text-xs">← Back</button>
            </form>
          )}

          {/* ─── STEP 3: PAY + UTR ─── */}
          {step === 'pay' && order && (
            <div className="space-y-4" data-testid="upi-pay-step">
              <div className="bg-gradient-to-br from-[#FFF0F3] to-[#FFE5EC] rounded-2xl p-4 text-center border border-[#FFD1DC]">
                <p className="text-[#4A2C35] font-bold text-lg">Pay ₹69 via UPI</p>
                <p className="text-[11px] text-[#8B6B75] mt-1">Send to <span className="font-semibold text-[#F4436C]">lovepair</span></p>
                <div className="mt-3 bg-white rounded-xl p-3 flex items-center justify-between gap-2 border border-[#FFE0E8]">
                  <p data-testid="upi-payee-id" className="text-[12px] font-mono text-[#4A2C35] break-all">{order.payee_vpa}</p>
                  <button onClick={copyUpiId} data-testid="copy-upi-btn" className="flex-shrink-0 text-[#F4436C] text-[11px] font-bold flex items-center gap-1">
                    <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              <button
                onClick={openUpi}
                data-testid="open-upi-btn"
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#FF8FAF] to-[#F4436C] text-white font-bold text-base shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                <ExternalLink size={18} />
                Open UPI App & Pay ₹69
              </button>
              {/* Backup native anchor — some Android browsers ignore window.location for UPI scheme */}
              <a
                href={order.upi_link}
                data-testid="open-upi-link"
                className="block w-full text-center py-2 text-[11px] text-[#F4436C] font-semibold underline"
              >
                Tap here if button doesn&apos;t open your UPI app
              </a>

              <div className="bg-[#FFFBF5] border border-[#FFE7B0] rounded-2xl p-3">
                <p className="text-[11px] font-bold text-[#8B6500] mb-1">📌 After payment:</p>
                <ol className="text-[11px] text-[#8B6500] space-y-0.5 ml-3 list-decimal">
                  <li>Open your UPI app</li>
                  <li>Tap the ₹69 transaction</li>
                  <li>Find the <strong>UTR / Transaction ID</strong> (12 digits)</li>
                  <li>Paste it below ⬇️</li>
                </ol>
              </div>

              <form onSubmit={submitUtr} className="space-y-3">
                <div>
                  <label className="text-[11px] font-semibold text-[#8B6B75] uppercase tracking-wider">UTR / Transaction ID</label>
                  <input
                    type="text" value={utr} maxLength={12} onChange={(e) => setUtr(e.target.value.replace(/\D/g, ''))}
                    placeholder="e.g. 412345678901" inputMode="numeric" required data-testid="upi-utr-input"
                    className="mt-1 w-full px-4 py-3 rounded-xl border border-[#FFE0E8] bg-white text-[#4A2C35] text-sm focus:outline-none focus:border-[#F4436C] font-mono tracking-wider"
                  />
                  <p className="text-[10px] text-[#B08EA0] mt-1">Find this in your UPI app under transaction details.</p>
                </div>

                {error && <div className="bg-red-50 text-red-600 text-xs rounded-xl p-3 text-center" data-testid="paywall-error">{error}</div>}

                <button
                  type="submit" disabled={loading}
                  data-testid="upi-submit-utr-btn"
                  className="w-full py-4 rounded-2xl bg-[#4A2C35] text-white font-bold text-base shadow-lg active:scale-[0.98] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check size={18} />}
                  {loading ? 'Submitting…' : 'Submit for Verification'}
                </button>
              </form>

              <button data-testid="upi-pay-back" onClick={() => setStep('details')} className="w-full py-2 text-[#8B6B75] text-xs">← Back</button>
            </div>
          )}

          {/* ─── STEP 4: PENDING ─── */}
          {step === 'pending' && (
            <div
              className="bg-blue-50 border border-blue-200 rounded-2xl p-5 text-center max-h-[60vh] overflow-y-auto"
              data-testid="paywall-pending-banner"
            >
              <div className="w-14 h-14 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-3">
                <Clock size={28} className="text-blue-600" />
              </div>
              <p data-testid="upi-review-status" className="text-blue-800 font-bold text-lg">{order?.status === 'rejected' ? 'Payment not verified' : 'Reference submitted'}</p>
              <p className="text-blue-700 text-xs mt-2 leading-relaxed">
                {order?.status === 'rejected' ? 'Contact support with your order ID and UTR so we can review the issue. Do not pay again until it is resolved.' : 'Our team will check your transfer. Premium activates after approval. Please do not pay again while your payment is under review.'}
              </p>
              <p className="text-blue-600 text-[11px] mt-3">
                {order?.status === 'rejected' ? 'Your reference remains available for support.' : 'This app checks for approval automatically, even if you close this window.'}
              </p>
              <p data-testid="upi-order-id" className="text-blue-700 text-[10px] mt-3 break-all">Order ID: {order?.order_id}</p>
              {paymentError && <p role="alert" data-testid="upi-status-error" className="text-red-700 text-xs mt-3">{paymentError}</p>}
              {order?.status !== 'rejected' && <button data-testid="upi-check-status" onClick={refreshPayment} disabled={checkingPayment} className="block mx-auto mt-4 text-blue-700 text-xs font-bold underline disabled:opacity-50">{checkingPayment ? 'Checking…' : 'Check status'}</button>}
              <a data-testid="upi-support-email" href={`mailto:lovepair369@gmail.com?subject=${encodeURIComponent(`Payment support ${order?.order_id}`)}`} className="block mt-3 text-xs text-blue-700 underline">Contact payment support</a>
              <button
                data-testid="upi-pending-close"
                onClick={closePaywall}
                className="mt-4 px-6 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold"
              >
                Got it, thanks!
              </button>
            </div>
          )}
          <div className="mt-5 pt-4 border-t border-[#FFE0E8]"><LegalLinks prefix="paywall" /></div>
        </div>
      </div>
      {showSaveModal && <SaveProgressModal onClose={() => setShowSaveModal(false)} />}
    </div>
  );
}
